/** @jsx jsx */
import { Button, Grid, Modal, Spacer } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { css, jsx } from "@emotion/core"
import React, { useEffect, useRef, useState } from "react"
import { useBetaFeatureAccess } from "../../Components/BetaFeatureAccess"
import { RemoveRowButton } from "../../Components/Buttons/RemoveRowButton"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { CreateProductForm } from "../../hooks/confirmationDialogues/useAssignItemsToCustomProducts"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { IRecord } from "../../types/schema"
import { useSettings } from "../../utils/useSettings"
import { isTempId } from "../../utils/utils"
import { BarscanColumn } from "./BarscanColumn"
import { IProductForSelect, ProductSelect } from "./ItemRowSageProduct"
import {
  BARSCAN_COLUMN_WIDTH,
  calculateDiscountPercent,
  calculateDiscountPrice,
  calculateMarginFromPrice,
  changeMarginField,
  DISCOUNT_COLUMN_WIDTH,
  IItem,
  IRowProps,
  itemTypes,
  MARGIN_COLUMN_WIDTH,
  PRICE_COLUMN_WIDTH,
  QUANTITY_COLUMN_WIDTH,
} from "./quoteOrderUtils"

export function CustomProductInput({
  registerItem,
  data,
  onChange,
  errors,
  rawOnChange,
  onRemove,
  renderModal,
  rawFunctions,
  quote,
  type,
  isCustomerViewing,
}: IRowProps) {
  const hasBetaFeatureAccess = useBetaFeatureAccess()
  const skeem = window.skeem
  const {
    useQuoteOrderStandardPricing,
    barScanOrders,
    warehouses,
    showDiscountOption,
    useExplicitAllocations,
    ...settings
  } = useSettings()
  const barscanIntegrated = barScanOrders && !quote.warehouseOverride?.disable_barscan
  const shouldShowDiscount = !!(showDiscountOption || quote.hasOpportunityCertificate)
  const { currentUser } = useGlobalState()
  const [focused, setFocused] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [replacementProduct, setReplacementProduct] = useState<IProductForSelect | null>(null)

  useEffect(() => {
    if (useQuoteOrderStandardPricing && data.standard_price_pennies) {
      rawFunctions.onChangeSilent({
        standard_discount_percent: calculateDiscountPercent(
          data.standard_price_pennies,
          data.price_pennies,
        ),
      })
    }
  }, [useQuoteOrderStandardPricing])

  useEffect(() => {
    const markUp = calculateMarginFromPrice(
      data.cost_price_pennies || undefined,
      data.price_pennies || undefined,
    )
    onChange({ markUp })
  }, [])

  function isEmptyItem(item: IItem) {
    const shownFields: (keyof IItem)[] = [
      "stock_code",
      "body",
      "price_pennies",
      "cost_price_pennies",
    ]
    return shownFields.every(field => !item[field] || String(item[field]).trim().length === 0)
  }

  const onCreateProduct = useAsyncCallback(async () =>
    renderModal({
      Component: ({ onResolve }) => (
        <Modal
          render={() => (
            <CreateProductForm
              data={data as any}
              onCancel={() => onResolve(false)}
              onSubmit={id => handleCreateProduct(id).then(() => onResolve(false))}
            />
          )}
          isOpen
        />
      ),
    }),
  )

  async function handleCreateProduct(productId: string) {
    const product = await skeem.fetch2<
      IRecord<
        "products",
        | "stock_code"
        | "body"
        | "cost_price_pennies"
        | "line_item_description"
        | "price_updated_at"
        | "sage_id"
      > & {
        branch: IRecord<"branches", "name">
        product_locations: IRecord<"product_locations", "name" | "free_stock" | "warehouse">[]
      }
    >({
      first: {
        name: "products",
        attributes: [
          "stock_code",
          "body",
          "cost_price_pennies",
          "line_item_description",
          "price_updated_at",
          "sage_id",
          { name: "branch", attributes: ["name"] },
          { name: "product_locations", attributes: ["name", "free_stock", "warehouse"] },
        ],
        filter: { matches: { id: productId } },
      },
    })
    rawOnChange({
      ...data,
      cost_price_pennies: product.cost_price_pennies,
      stock_code: product.stock_code,
      body: product.body,
      product,
      type: itemTypes.sage,
      product_location: product.product_locations[0],
    })
  }

  useEffect(() => {
    if (!settings._loaded) {
      return
    }
    return registerItem({
      validate(item) {
        const warnings = []
        const errors: any = {}
        if (isEmptyItem(item)) {
          // row is empty
          warnings.push("A custom product row has not been filled in and will be removed")
        } else {
          if (item.stock_code && item.stock_code.length > 30) {
            errors.stock_code = "Max 30 Characters"
          }
          if (item.stock_code && /\s/g.exec(item.stock_code)) {
            errors.stock_code = "Cannot have spaces"
          }
          if (!item.quantity) {
            errors.quantity = "Required"
          }
          if (!item.price_pennies && !settings.allowZeroPriceQuotesOrders) {
            errors.price_pennies = "Required"
          }
          if (!item.cost_price_pennies) {
            errors.cost_price_pennies = "Required"
          }

          if (item.price_pennies && item.cost_price_pennies) {
            if (item.cost_price_pennies > item.price_pennies) {
              warnings.push("A custom product has a cost price higher then what you have quoted")
            }
          }

          if ((item?.price_pennies || 0) < 0) {
            errors.price_pennies = "Cannot be negative"
          }
        }
        return { errors, warnings }
      },
      onSave(item) {
        if (isEmptyItem(item)) {
          return { id: item.id, $deleted: true }
        }
        return { ...item, markUp: Number(item.markUp || 0), quantity: Number(item.quantity || 1) }
      },
    })
  }, [settings._loaded])

  ///////////////////////////////
  ///////////////////////////////
  ///////////////////////////////

  function handleStandardPriceChange(price?: number | null) {
    const item = {
      price_pennies: price,
      standard_discount_percent: calculateDiscountPercent(data.standard_price_pennies, price),
      markUp: calculateMarginFromPrice(data.cost_price_pennies || undefined, price ?? 0),
    }
    onChange(item)
  }

  function handleStandardDiscountChange(discountPct?: number | null) {
    const calculatedPrice = calculateDiscountPrice(data.standard_price_pennies, discountPct)
    const item = {
      price_pennies: calculatedPrice,
      standard_discount_percent: discountPct,
      markUp: calculateMarginFromPrice(data.cost_price_pennies || undefined, calculatedPrice),
    }
    onChange(item)
  }

  function handleStandardCostPricePenniesChange(price: number) {
    const item = {
      standard_price_pennies: price,
      discountPercent: calculateDiscountPercent(price, data.price_pennies || undefined),
      markUp: calculateMarginFromPrice(price, data.price_pennies || undefined),
    }
    onChange(item)
  }

  function handleCostPricePenniesChange(price: number) {
    onChange({
      cost_price_pennies: price,
      markUp: calculateMarginFromPrice(price, data.price_pennies || undefined),
    })
  }

  function handleQuotedPriceChange(price: number) {
    onChange({
      price_pennies: price,
      markUp: calculateMarginFromPrice(data.cost_price_pennies || undefined, price),
    })
  }

  function handleMarkupChange(markUp: number | null) {
    if (markUp) {
      const price_pennies = Number(
        ((data.cost_price_pennies || 0) / ((100 - (Number(markUp) || 0)) / 100)).toFixed(0),
      )
      onChange({ price_pennies, markUp })
    } else {
      const price_pennies = data.cost_price_pennies
      onChange({ price_pennies, markUp: markUp || undefined })
    }
  }

  function handleFocus() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setFocused(true)
  }

  function handleBlur() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => setFocused(false), 400)
  }

  async function handleSubmitReplacementProduct() {
    if (!replacementProduct) {
      return
    }

    const product = await skeem.fetchOne<
      IRecord<
        "products",
        | "stock_code"
        | "body"
        | "category"
        | "cost_price_pennies"
        | "line_item_description"
        | "price_updated_at"
        | "sage_id"
      > & {
        branch: IRecord<"branches", "name">
        product_locations: IRecord<"product_locations", "name" | "free_stock" | "warehouse">[]
      }
    >({
      products: {
        attributes: [
          "stock_code",
          "body",
          "category",
          "cost_price_pennies",
          "line_item_description",
          "price_updated_at",
          "sage_id",
          { name: "branch", attributes: ["name"] },
          {
            name: "product_locations",
            attributes: ["name", "free_stock", "warehouse"],
          },
        ],
        filter: { matches: { id: replacementProduct.id } },
      },
    })

    const warehouseIds = warehouses.map(w => w.id)
    const locations =
      warehouses?.length > 0
        ? product.product_locations.filter(item =>
            item.warehouse ? warehouseIds.includes(item.warehouse.id) : false,
          )
        : product.product_locations

    rawOnChange({
      ...data,
      type: itemTypes.sage,
      stock_code: product.stock_code,
      body: product.body,
      category: product.category,
      cost_price_pennies: product.cost_price_pennies,
      markUp: null,
      product,
      product_location: locations[0],
    })
  }

  let markUp
  if (typeof data.markUp === "number") {
    markUp = data.markUp
  } else {
    // intial load
    markUp = calculateMarginFromPrice(
      data.cost_price_pennies || undefined,
      data.price_pennies || undefined,
    )
  }

  const [rawMargin, setRawMargin] = useState(typeof markUp === "number" ? markUp : null)
  useEffect(() => setRawMargin(data.markUp), [data.markUp])

  return (
    <React.Fragment>
      <div tabIndex={-1} onFocus={handleFocus} onBlur={handleBlur}>
        <Grid gap={12}>
          <div>
            <Grid
              columns={[
                { fr: 1 },
                { fr: 2 },
                barscanIntegrated && BARSCAN_COLUMN_WIDTH + 150,
                QUANTITY_COLUMN_WIDTH,
                useExplicitAllocations && QUANTITY_COLUMN_WIDTH,
                showDiscountOption && DISCOUNT_COLUMN_WIDTH,
                !isCustomerViewing && PRICE_COLUMN_WIDTH,
                PRICE_COLUMN_WIDTH,
                (useQuoteOrderStandardPricing || !isCustomerViewing) && MARGIN_COLUMN_WIDTH,
              ].filter(Boolean)}
              gap={6}
            >
              <div>
                <Fields.Text
                  placeholder="Product code: ABC001"
                  value={data.stock_code}
                  onChange={val =>
                    onChange({
                      stock_code: val,
                    })
                  }
                  onBlur={e => e && onChange({ stock_code: e.target.value.trim() })}
                  inputProps={{
                    autoFocus: isTempId(data.id),
                  }}
                  error={errors.stock_code}
                />
              </div>
              <Fields.TextArea
                placeholder="Description..."
                value={data.body}
                error={errors.body}
                onChange={val => onChange({ body: val })}
                inputProps={{ style: { height: 40 } }}
                onBlur={() => onChange({ body: data.body ?? "" })}
              />
              {barscanIntegrated && (
                <div>
                  <BarscanColumn onChange={onChange} values={data} />
                </div>
              )}
              <Fields.Number
                placeholder="99"
                value={data.quantity || data.quantity === 0 ? String(data.quantity) : null}
                onChange={val => onChange({ quantity: Number(val) })}
                inputProps={{ min: 1 }}
                error={errors.quantity}
              />
              {useExplicitAllocations && <div></div>}
              {showDiscountOption && (
                <Fields.Number
                  prefix="%"
                  placeholder="10"
                  value={
                    data.discountPercent || data.discountPercent === 0
                      ? String(data.discountPercent)
                      : null
                  }
                  onChange={val => onChange({ discountPercent: Number(val) })}
                  inputProps={{ min: 0 }}
                  error={errors.discountPercent}
                />
              )}
              {!isCustomerViewing && (
                <div>
                  {useQuoteOrderStandardPricing ? (
                    <Fields.Price
                      placeholder="123.45"
                      value={data.standard_price_pennies ?? null}
                      onChange={handleStandardCostPricePenniesChange}
                      error={errors.standard_price_pennies}
                      prefix={false}
                      inPennies
                    />
                  ) : null}
                  <Fields.Price
                    label={useQuoteOrderStandardPricing ? "Cost Price" : null}
                    placeholder="123.45"
                    value={data.cost_price_pennies}
                    onChange={handleCostPricePenniesChange}
                    error={errors.cost_price_pennies}
                    prefix={false}
                    inPennies
                  />
                </div>
              )}

              <Fields.Price
                placeholder="123.45"
                value={data.price_pennies}
                onChange={
                  useQuoteOrderStandardPricing ? handleStandardPriceChange : handleQuotedPriceChange
                }
                error={errors.price_pennies}
                prefix={false}
                inPennies
              />
              {useQuoteOrderStandardPricing ? (
                <Fields.Number
                  placeholder="10"
                  value={
                    data.standard_discount_percent || data.standard_discount_percent === 0
                      ? String(data.standard_discount_percent)
                      : null
                  }
                  onChange={val => handleStandardDiscountChange(Number(val))}
                  inputProps={{ min: 0 }}
                  error={errors.standard_discount_percent}
                />
              ) : isCustomerViewing ? null : (
                <Fields.Number2
                  placeholder="0"
                  value={rawMargin}
                  onChange={setRawMargin}
                  onBlur={() => {
                    if (rawMargin === data.markUp) {
                      return
                    }
                    const margin = Math.min(Math.max(rawMargin ?? 0, 0.01), 99.99)
                    setRawMargin(margin)
                    changeMarginField(margin, handleMarkupChange)
                  }}
                  inputProps={{ min: 0.01, max: 99.99, step: 0.01 }}
                />
              )}
            </Grid>
          </div>
          {type === "quote" && (
            <Fields.Checkbox
              label="This is a special order"
              value={!!data.special_order_item}
              onChange={value => {
                onChange({ special_order_item: value })
              }}
            />
          )}
          {focused && (
            <div
              css={css`
                background-color: rgba(0, 0, 0, 0.1);
                padding: 16px 32px;
              `}
            >
              {currentUser.canCreateSageProducts ? (
                <i>
                  You can use custom product lines to represent products which don't yet exist in
                  Sage. However, these items cannot be copied over to Orders, Invoices or
                  Deliveries. Instead you should create the new product in Sage, then use the
                  drop-down to replace this line with a normal product line or using the "Create new
                  Sage product button", create a new product from this custom product.
                </i>
              ) : (
                <i>
                  You can use custom product lines to represent products which don't yet exist in
                  Sage. However, these items cannot be copied over to Orders, Invoices or
                  Deliveries. Instead you should create the new product in Sage, then use the
                  drop-down to replace this line with a normal product line.
                </i>
              )}
              <Spacer />
              <Grid
                columns={["max-content", 300, "max-content", "max-content"]}
                alignItems="center"
                gap={12}
              >
                <span>Replace with Sage Product:</span>
                <div>
                  <ProductSelect value={replacementProduct} onChange={setReplacementProduct} />
                </div>
                <Button onClick={handleSubmitReplacementProduct} disabled={!replacementProduct}>
                  Replace
                </Button>
                {currentUser.canCreateSageProducts && (
                  <Button
                    onClick={() => {
                      onCreateProduct()
                    }}
                  >
                    Create new Sage product
                  </Button>
                )}
              </Grid>
            </div>
          )}
        </Grid>
        {hasBetaFeatureAccess("richQuoteEmail") && type == "quote" && (
          <div
            css={css`
              padding: 8px 0 0 0;
            `}
          >
            <Fields.Checkbox
              label="Optional"
              onChange={() => onChange({ is_rich_quote_optional: !data.is_rich_quote_optional })}
              value={!!data.is_rich_quote_optional}
            />
          </div>
        )}
      </div>
      <RemoveRowButton onRemove={onRemove} />
    </React.Fragment>
  )
}
